import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Select, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface'
import { useHistory } from 'react-router'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import SearchInput from '@/components/search-input/search-input'
import { Row, Expaned } from '@/components/layout/layout'
import Auth from '@/views/main/components/auth/auth'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import { IMember, IMemberGroupListItem, IMemberLevelDetail, IMemberListOrderBy } from '@/model/types/member'
import { changeMembershipGroup, deleteMember, getMemberGroupList, getMemberLevelList, getMemberList } from '../api'
import { memberManageModel } from '../model'
import { MEMBER_IMPORT, MEMBER_LIST } from '../router'
import { getMemberTablePagination, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'
import MemberHeaderActions from '../member-header-actions'
import MemberDetail from '../member-detail/member-detail'
import AddMemberModal from './add-member-modal'
import MemberExport from './member-export'
import MemberFilter, { getDefaultMemberFilterValue, MemberFilterValue } from './member-filter'
import { canDeleteMember, getMemberBalanceValue, getMemberStatisticValue } from './member-list-utils'

import './member-list.scss'

const MEMBER_FILTER_TYPE = {
    group: 0,
    level: 1,
    pay: 2,
    memberType: 3,
    other: 4,
    balanceRange: 5,
    pointsRange: 6,
}

const getOrderBy = (field?: string | number): IMemberListOrderBy | undefined => {
    if (field === 'points') return 'point'
    if (field === 'accumulated_recharge' || field === 'accumulated_consume' || field === 'accumulated_consume_count') {
        return field
    }
    if (field === 'birthday' || field === 'balance') return field

    return undefined
}

const getSorterField = (field: SorterResult<IMember>['field']) => {
    if (typeof field === 'string' || typeof field === 'number') return field

    return undefined
}

const getMemberFilterParams = (filter: MemberFilterValue) => {
    const filters = []

    if (filter.groupIds.length) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.group,
            group_ids: filter.groupIds,
        })
    }
    if (filter.memberLevels.length) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.level,
            member_levels: filter.memberLevels,
        })
    }
    if (filter.memberPay.length) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.pay,
            member_pay: filter.memberPay,
        })
    }
    if (filter.memberTypes.length) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.memberType,
            member_types: filter.memberTypes,
        })
    }
    if (filter.otherFilter.length) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.other,
            other_filter: filter.otherFilter,
        })
    }
    if (filter.balanceMin !== undefined || filter.balanceMax !== undefined) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.balanceRange,
            balance_min: filter.balanceMin,
            balance_max: filter.balanceMax,
        })
    }
    if (filter.pointsMin !== undefined || filter.pointsMax !== undefined) {
        filters.push({
            filter_type: MEMBER_FILTER_TYPE.pointsRange,
            points_min: filter.pointsMin,
            points_max: filter.pointsMax,
        })
    }

    return filters.length ? filters : undefined
}

const MemberList: React.FC = () => {
    const history = useHistory()
    const [keyword, setKeyword] = useState('')
    const [searchKey, setSearchKey] = useState(0)
    const [filter, setFilter] = useState<MemberFilterValue>(getDefaultMemberFilterValue)
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
    const [addVisible, setAddVisible] = useState(false)
    const [exportVisible, setExportVisible] = useState(false)
    const [batchVisible, setBatchVisible] = useState(false)
    const [batchGroupId, setBatchGroupId] = useState<number | undefined>()
    const [batchLoading, setBatchLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [sorter, setSorter] = useState<SorterResult<IMember>>({})
    const [refreshKey, setRefreshKey] = useState(0)

    const refreshList = useCallback(() => {
        memberManageModel.refreshMemberList()
        setRefreshKey(val => val + 1)
    }, [])
    const resetList = () => {
        setPage(1)
        setKeyword('')
        setSearchKey(value => value + 1)
        setFilter(getDefaultMemberFilterValue())
        setSorter({})
        setSelectedRowKeys([])
        refreshList()
    }

    const filterError = useMemo(() => {
        if (
            filter.balanceMin !== undefined &&
            filter.balanceMax !== undefined &&
            filter.balanceMin > filter.balanceMax
        ) {
            return '余额下限不能大于余额上限'
        }
        if (filter.pointsMin !== undefined && filter.pointsMax !== undefined && filter.pointsMin > filter.pointsMax) {
            return '积分下限不能大于积分上限'
        }

        return ''
    }, [filter])

    const groupsReq = useRequest(getMemberGroupList, {
        onError: e => $msg('error', e.message || '获取会员分组失败'),
    })
    const levelsReq = useRequest(getMemberLevelList, {
        onError: e => $msg('error', e.message || '获取会员等级失败'),
    })

    const filterText = useMemo(() => {
        const textList: string[] = []
        const groupNameMap = new Map(
            (groupsReq.data || []).map((group: IMemberGroupListItem) => [group.id, group.name]),
        )
        const levelNameMap = new Map(
            (levelsReq.data || []).map((level: IMemberLevelDetail) => [level.level, level.name]),
        )
        const payMap: { [key: number]: string } = { 0: '付费会员正常', 1: '付费会员过期', 2: '付费会员未支付' }
        const typeMap: { [key: number]: string } = {
            0: '小程序会员',
            1: '手工会员',
            2: '老零售会员',
            3: '表格导入会员',
        }
        const otherMap: { [key: number]: string } = {
            0: '收银台消费过',
            1: '商城消费过',
            2: '充值过',
            3: '有会员余额',
        }

        if (filter.groupIds.length) {
            textList.push(`分组：${filter.groupIds.map(id => groupNameMap.get(id) || id).join('、')}`)
        }
        if (filter.memberLevels.length) {
            textList.push(`等级：${filter.memberLevels.map(level => levelNameMap.get(level) || level).join('、')}`)
        }
        if (filter.memberPay.length) {
            textList.push(
                filter.memberPay
                    .map(value => payMap[value])
                    .filter(Boolean)
                    .join('、'),
            )
        }
        if (filter.memberTypes.length) {
            textList.push(
                filter.memberTypes
                    .map(value => typeMap[value])
                    .filter(Boolean)
                    .join('、'),
            )
        }
        if (filter.otherFilter.length) {
            textList.push(
                filter.otherFilter
                    .map(value => otherMap[value])
                    .filter(Boolean)
                    .join('、'),
            )
        }
        if (filter.balanceMin !== undefined || filter.balanceMax !== undefined) {
            textList.push(`余额：${filter.balanceMin ?? 0}-${filter.balanceMax ?? '不限'}`)
        }
        if (filter.pointsMin !== undefined || filter.pointsMax !== undefined) {
            textList.push(`积分：${filter.pointsMin ?? 0}-${filter.pointsMax ?? '不限'}`)
        }

        return textList.filter(Boolean).join('；')
    }, [filter, groupsReq.data, levelsReq.data])

    const listReq = useRequest(
        () =>
            getMemberList({
                page,
                limit: pageSize,
                keyword: keyword || undefined,
                member_filter: filterError ? undefined : getMemberFilterParams(filter),
                order_by: getOrderBy(getSorterField(sorter.field)),
                desc: sorter.order ? sorter.order === 'descend' : undefined,
            }),
        {
            refreshDeps: [page, pageSize, keyword, filter, sorter, refreshKey],
            ready: !filterError,
            onError: e => $msg('error', e.message || '获取会员列表失败'),
        },
    )

    useEffect(() => {
        if (filterError) {
            $msg('warning', filterError)
        }
    }, [filterError])

    const onBatchGroupChange = async () => {
        if (!selectedRowKeys.length) {
            $msg('warning', '请先勾选会员')

            return
        }
        if (batchGroupId === undefined) {
            $msg('warning', '请选择目标分组')

            return
        }
        setBatchLoading(true)
        try {
            await changeMembershipGroup({
                membership_id_list: selectedRowKeys,
                group_id: batchGroupId,
            })
            $msg('success', '批量改分组成功')
            setSelectedRowKeys([])
            setBatchVisible(false)
            setBatchGroupId(undefined)
            refreshList()
        } catch (e) {
            $msg('error', e.message || '批量改分组失败')
        } finally {
            setBatchLoading(false)
        }
    }

    const columns: ColumnsType<IMember> = useMemo(
        () => [
            {
                title: '#',
                key: 'index',
                width: 56,
                render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
            },
            {
                title: '会员',
                dataIndex: 'name',
                width: 168,
                render: (value, record) => value || record.user?.nickname || '-',
            },
            {
                title: '手机号',
                dataIndex: 'phone',
                width: 152,
                render: value => value || '-',
            },
            {
                title: '会员分组',
                width: 132,
                render: (_value, record) => record.group?.name || '-',
            },
            {
                title: '等级',
                width: 112,
                render: (_value, record) => record.level?.name || '-',
            },
            {
                title: '生日',
                dataIndex: 'birthday',
                width: 126,
                sorter: true,
                render: value => value || '-',
            },
            {
                title: '余额',
                dataIndex: 'balance',
                width: 112,
                sorter: true,
                render: (_value, record) => getMemberBalanceValue(record),
            },
            {
                title: '积分',
                dataIndex: 'points',
                width: 96,
                sorter: true,
                render: value => value || 0,
            },
            {
                title: '累计充值',
                dataIndex: 'accumulated_recharge',
                width: 130,
                sorter: true,
                render: (_value, record) =>
                    getMemberStatisticValue(record, 'accumulated_recharge', 'total_recharge_amount'),
            },
            {
                title: '累计消费',
                dataIndex: 'accumulated_consume',
                width: 130,
                sorter: true,
                render: (_value, record) =>
                    getMemberStatisticValue(record, 'accumulated_consume', 'total_consume_amount'),
            },
            {
                title: '消费次数',
                dataIndex: 'accumulated_consume_count',
                width: 112,
                sorter: true,
                render: (_value, record) =>
                    getMemberStatisticValue(record, 'accumulated_consume_count', 'total_consume_times'),
            },
            {
                title: '操作',
                width: 100,
                fixed: 'right',
                render: (_value, record) => (
                    <Row>
                        <span
                            className="action"
                            onClick={() =>
                                showLayer(<MemberDetail drawer memberId={record.id} onSuccess={refreshList} />)
                            }
                        >
                            详情
                        </span>
                        {canDeleteMember(record) ? (
                            <Auth
                                perssion={28}
                                onClick={() =>
                                    showLayer(
                                        <SgModal
                                            headerStyles={{ width: '440px' }}
                                            isShow
                                            type="danger"
                                            style={{ height: '156px' }}
                                            onClose={() => layerManage.pop()}
                                            onCancel={() => layerManage.pop()}
                                            onConfirm={() => {
                                                layerManage.pop()
                                                deleteMember(record.id)
                                                    .then(() => {
                                                        $msg('success', '删除会员成功')
                                                        refreshList()
                                                    })
                                                    .catch(e => $msg('error', e.message || '删除会员失败'))
                                            }}
                                        >
                                            <div>
                                                <div data-font-size-20 data-font-weight-600 data-color-000>
                                                    确认删除手工会员
                                                    <span data-color-ff4343 data-margin-h-6>
                                                        {record.name || record.phone}
                                                    </span>
                                                    吗？
                                                </div>
                                            </div>
                                        </SgModal>,
                                    )
                                }
                            >
                                <span className="action danger">删除</span>
                            </Auth>
                        ) : null}
                    </Row>
                ),
            },
        ],
        [page, pageSize, refreshList],
    )

    return (
        <Expaned className="member-list">
            <MemberHeaderActions>
                <Auth perssion={19} onClick={() => setAddVisible(true)}>
                    <Button className="member-manage__toolbar-button" types="default">
                        添加会员
                    </Button>
                </Auth>
                {selectedRowKeys.length ? (
                    <Auth perssion={19} onClick={() => setBatchVisible(true)}>
                        <Button className="member-manage__toolbar-button" types="primary">
                            批量改分组({selectedRowKeys.length})
                        </Button>
                    </Auth>
                ) : (
                    <Tooltip title="请先勾选要调整分组的会员">
                        <span>
                            <Button className="member-manage__toolbar-button" types="primary" disabled>
                                批量改分组
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Auth
                    perssion={19}
                    onClick={() =>
                        history.push({
                            pathname: MEMBER_IMPORT,
                            state: { backPath: MEMBER_LIST, backText: '返回列表' },
                        })
                    }
                >
                    <Button className="member-manage__toolbar-button" types="primary">
                        批量导入
                    </Button>
                </Auth>
                <Button
                    className="member-manage__toolbar-button"
                    types="primary"
                    onClick={() => setExportVisible(true)}
                >
                    会员导出
                </Button>
                <Button className="member-manage__toolbar-button" types="primary" onClick={resetList}>
                    重置
                </Button>
                <Button
                    className="member-manage__toolbar-button"
                    types="primary"
                    loading={listReq.loading}
                    onClick={refreshList}
                >
                    刷新
                </Button>
                <div
                    className={`member-list__filter-trigger ${filterText ? 'member-list__filter-trigger--active' : ''}`}
                    data-cursor-pointer
                    onClick={() =>
                        showLayer(
                            <MemberFilter
                                value={filter}
                                groups={groupsReq.data || []}
                                levels={levelsReq.data || []}
                                onConfirm={val => {
                                    setPage(1)
                                    setSelectedRowKeys([])
                                    setFilter(val)
                                }}
                            />,
                        )
                    }
                >
                    <i className="iconfont icon6shaixuan-20" data-font-size-20 />
                    <span data-margin-left-8>筛选</span>
                </div>
                <div className="member-list__search">
                    <SearchInput
                        key={searchKey}
                        placeholder="输入会员昵称/姓名/手机号"
                        onSearch={val => {
                            setPage(1)
                            setSelectedRowKeys([])
                            setKeyword(val)
                        }}
                        onDelete={() => {
                            setPage(1)
                            setSelectedRowKeys([])
                            setKeyword('')
                        }}
                    />
                </div>
            </MemberHeaderActions>
            {(keyword || filterText) && (
                <Row className="member-list__search-box">
                    <Row>
                        {keyword ? (
                            <div>
                                搜索：
                                <span className="them-color" data-font-weight-600 data-padding-h-8>
                                    {keyword}
                                </span>
                            </div>
                        ) : null}
                        {filterText ? (
                            <div>
                                筛选：
                                <span className="them-color" data-font-weight-600 data-padding-h-8>
                                    {filterText}
                                </span>
                            </div>
                        ) : null}
                    </Row>
                    <div
                        className="member-list__search-box-delete"
                        onClick={() => {
                            setPage(1)
                            setKeyword('')
                            setSearchKey(value => value + 1)
                            setSelectedRowKeys([])
                            setFilter(getDefaultMemberFilterValue())
                        }}
                    >
                        <i data-cursor-pointer className="iconfont icon6guanbi-20" />
                    </div>
                </Row>
            )}
            <Expaned data-column data-overflow-hidden className="member-list__content">
                <div className="table-container member-list__panel">
                    <Table
                        bordered
                        rowKey={record => record.id}
                        loading={listReq.loading}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: keys => setSelectedRowKeys(keys as number[]),
                        }}
                        columns={columns}
                        dataSource={listReq.data?.member_list || []}
                        pagination={getMemberTablePagination({
                            current: page,
                            pageSize,
                            total: listReq.data?.member_count || 0,
                            onChange: nextPage => {
                                setSelectedRowKeys([])
                                setPage(nextPage)
                            },
                            onShowSizeChange: (nextPage, nextSize) => {
                                setSelectedRowKeys([])
                                setPage(nextPage)
                                setPageSize(nextSize)
                            },
                        })}
                        onChange={(_pagination: TablePaginationConfig, _filters, nextSorter) => {
                            setSelectedRowKeys([])
                            setSorter(nextSorter as SorterResult<IMember>)
                        }}
                        scroll={{
                            x: 'max-content',
                            y: keyword || filterText ? 'calc(100vh - 300px)' : 'calc(100vh - 260px)',
                        }}
                    />
                </div>
            </Expaned>
            <AddMemberModal
                visible={addVisible}
                groups={groupsReq.data || []}
                onCancel={() => setAddVisible(false)}
                onSuccess={() => {
                    setAddVisible(false)
                    refreshList()
                }}
            />
            <MemberExport visible={exportVisible} onCancel={() => setExportVisible(false)} />
            {batchVisible ? (
                <SgModal
                    title="批量改分组"
                    isShow
                    type="success"
                    loading={batchLoading}
                    onConfirm={onBatchGroupChange}
                    onClose={() => {
                        setBatchVisible(false)
                        setBatchGroupId(undefined)
                    }}
                    onCancel={() => {
                        setBatchVisible(false)
                        setBatchGroupId(undefined)
                    }}
                    okText="确认"
                    cancelText="取消"
                >
                    <div className="member-list__batch-group-modal">
                        <div className="member-list__batch-group-tip">
                            已选择 {selectedRowKeys.length} 位会员，确认后会统一调整到目标分组。
                        </div>
                        <div className="member-list__batch-group-label">目标分组</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="请选择目标分组"
                            value={batchGroupId}
                            onChange={setBatchGroupId}
                        >
                            {(groupsReq.data || []).map((group: IMemberGroupListItem) => (
                                <Select.Option key={group.id} value={group.id}>
                                    {group.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </SgModal>
            ) : null}
        </Expaned>
    )
}

export default MemberList
